import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Checkbox, EmptyState, ErrorState, Icon, TableSkeletonRows, toast, type IconName } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { isPlanGateError, UpgradeCard } from "../shared/plan-gate";
import type { AudienceOut, MatrixSource } from "../shared/types";
import { changedEntries, draftFrom, isLastAudience, toggleCell, type MatrixDraft } from "./matrix-model";
import { useAccessMatrix, useCanManageAudiences, useSaveAccessMatrix } from "./use-audiences-data";

const TYPE_ICON: Readonly<Record<string, IconName>> = { file: "file-text", website: "globe", qa: "message-circle", text: "scroll-text" };

function SourceCell({ source }: { source: MatrixSource }) {
  const { t } = useTranslation();
  const attention = source.status === "failed";
  return (
    <div className="flex items-center gap-2.5">
      <Icon name={attention ? "alert-triangle" : (TYPE_ICON[source.type] ?? "file")} className={attention ? "size-4 shrink-0 text-warning" : "size-4 shrink-0 text-primary-ink"} />
      <div className="min-w-0">
        <div className="truncate font-semibold">{source.title}</div>
        {attention ? <div className="text-[11px] text-warning">{t("deployKnowledge.audiences.matrixFailed")}</div> : null}
        {source.inherit_audiences ? <div className="text-[11px] text-fg-subtle">{t("deployKnowledge.audiences.matrixInherits")}</div> : null}
      </div>
    </div>
  );
}

interface RowProps {
  source: MatrixSource;
  audiences: readonly AudienceOut[];
  draft: MatrixDraft;
  canManage: boolean;
  onToggle: (sourceId: string, code: number) => void;
}

function MatrixRow({ source, audiences, draft, canManage, onToggle }: RowProps) {
  const { t } = useTranslation();
  const codes = draft[source.source_id] ?? [];
  return (
    <tr>
      <td>
        <SourceCell source={source} />
      </td>
      {audiences.map((audience) => {
        const last = isLastAudience(draft, source.source_id, audience.code);
        return (
          <td key={audience.code} className="text-center">
            <Checkbox
              checked={codes.includes(audience.code)}
              disabled={!canManage || last}
              title={last ? t("deployKnowledge.audiences.matrixLast") : undefined}
              aria-label={t("deployKnowledge.audiences.matrixCell", { source: source.title, audience: audience.label })}
              onCheckedChange={() => { onToggle(source.source_id, audience.code); }}
            />
          </td>
        );
      })}
    </tr>
  );
}

function NoSources() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <EmptyState
      icon="database"
      headingLevel={3}
      title={t("deployKnowledge.audiences.matrixEmptyTitle")}
      description={t("deployKnowledge.audiences.matrixEmptyBody")}
      action={
        <Button asChild>
          <AppLink href={consoleRoutes.agents(summary.slug)}>
            <Icon name="plus" />
            {t("deployKnowledge.audiences.matrixEmptyCta")}
          </AppLink>
        </Button>
      }
    />
  );
}

/** Access matrix tab: sources × audiences, saving only the changed rows. */
export function AccessMatrixPanel() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const matrix = useAccessMatrix();
  const save = useSaveAccessMatrix();
  const canManage = useCanManageAudiences();
  const [edits, setEdits] = useState<MatrixDraft | null>(null);
  if (matrix.isError && isPlanGateError(matrix.error)) return <UpgradeCard feature={t("deployKnowledge.audiences.tabMatrix")} />;
  const sources = matrix.data?.sources ?? [];
  const audiences = matrix.data?.audiences ?? [];
  const draft = edits ?? draftFrom(sources);
  const changed = changedEntries(sources, draft);
  const onSave = () => {
    save.mutate(changed, {
      onSuccess: (result) => {
        setEdits(null);
        toast.success(t("deployKnowledge.audiences.matrixSaved"), {
          description: t("deployKnowledge.audiences.matrixAffected", { count: result.affected_agents?.length ?? 0 }),
        });
      },
      onError: (caught) => toast.error(message(caught)),
    });
  };
  return (
    <section className="card overflow-hidden" aria-labelledby="matrix-title">
      <div className="card-header flex-wrap gap-3">
        <div>
          <h2 id="matrix-title" className="card-title">
            {t("deployKnowledge.audiences.tabMatrix")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("deployKnowledge.audiences.matrixHint")}</p>
        </div>
        {canManage ? (
          <div className="flex items-center gap-2">
            {changed.length > 0 ? (
              <Badge tone="warning" dot>
                {t("deployKnowledge.unsavedChanges")}
              </Badge>
            ) : null}
            <Button size="sm" onClick={onSave} disabled={changed.length === 0} loading={save.isPending}>
              <Icon name="check" />
              {t("deployKnowledge.audiences.matrixSave")}
            </Button>
          </div>
        ) : null}
      </div>
      {matrix.isError ? (
        <ErrorState title={t("deployKnowledge.loadError")} description={t("deployKnowledge.loadErrorBody")} onRetry={() => void matrix.refetch()} retryLabel={t("common.retry")} />
      ) : !matrix.isPending && sources.length === 0 ? (
        <NoSources />
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t("deployKnowledge.audiences.matrixSource")}</th>
                {audiences.map((audience) => (
                  <th key={audience.code} className="text-center">
                    {audience.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.isPending ? (
                <TableSkeletonRows rows={6} columns={5} />
              ) : (
                sources.map((source) => (
                  <MatrixRow
                    key={source.source_id}
                    source={source}
                    audiences={audiences}
                    draft={draft}
                    canManage={canManage}
                    onToggle={(sourceId, code) => { setEdits(toggleCell(draft, sourceId, code)); }}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-line px-5 py-3 text-xs text-fg-subtle">
        <span className="flex items-center gap-1.5">
          <Icon name="lock" className="size-3.5" />
          {t("deployKnowledge.audiences.matrixNoteRestricted")}
        </span>
        <span className="flex items-center gap-1.5">
          <Icon name="alert-triangle" className="size-3.5 text-warning" />
          {t("deployKnowledge.audiences.matrixNoteOcr")}
        </span>
      </div>
    </section>
  );
}
